import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Toaster } from './ui/toaster';
import { useToast } from '../hooks/use-toast';
import {
  Clock, LogOut, Loader2, CheckCircle2, AlertTriangle, Trash2, Pencil, X, ArrowLeft,
  CloudOff, Upload,
} from 'lucide-react';
import { mobileAPI, timeEntryAPI, projectAPI, authAPI, toLocalDateString } from '../services/api';
import { TASKS, DEFAULT_HOURS, hoursToTimeStr, timeStrToHours, formatHours, formatDay } from '../utils/timeEntries';
import { nouvelIdentifiant, mettreEnAttente, lister, retirer, vider, disponible as fileDisponible }
  from '../utils/filePointages';

// Écran de terrain : saisir ses heures depuis un téléphone, et relire/corriger
// les dernières saisies. Il ne demande au serveur que /api/mobile/context et
// /api/time-entries : aucun montant, aucun coût, aucune marge n'y transite.
const MobileTimeEntry = ({ user, onLoggedOut, onRetour }) => {
  const { toast } = useToast();
  // Le patron saisit pour l'équipe comme un chef ; seul le salarié est limité à
  // lui-même. Sans cela, le patron n'aurait personne à cocher : aucune fiche
  // salarié n'est reliée à son compte.
  const pourEquipe = user.role !== 'salarie';

  const [contexte, setContexte] = useState(null);
  const [onglet, setOnglet] = useState('saisie');
  const [date, setDate] = useState(toLocalDateString(new Date()));
  const [projectId, setProjectId] = useState('');
  const [task, setTask] = useState('');
  const [comment, setComment] = useState('');
  const [problem, setProblem] = useState(false);
  const [heures, setHeures] = useState({});        // employeeId -> « 08:45 »
  const [choisis, setChoisis] = useState([]);      // employeeId cochés
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [saisies, setSaisies] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [correction, setCorrection] = useState(null);
  const [enAttente, setEnAttente] = useState([]);   // saisies gardées dans le téléphone
  const [envoiFile, setEnvoiFile] = useState(false);

  const moi = contexte?.employees?.find((e) => e.id === user.employeeId) || null;

  useEffect(() => {
    mobileAPI.context()
      .then((data) => {
        setContexte(data);
        if (!pourEquipe && data.user?.employeeId) {
          setChoisis([data.user.employeeId]);
          setHeures({ [data.user.employeeId]: hoursToTimeStr(DEFAULT_HOURS) });
        }
      })
      .catch(() => toast({ title: 'Chargement impossible', variant: 'destructive' }));
  }, [pourEquipe, toast]);

  const rafraichirAttente = async () => {
    if (!fileDisponible()) return;
    try { setEnAttente(await lister()); } catch (e) { console.warn('File locale illisible', e); }
  };

  // Envoie ce qui attend. Appelé au démarrage, au retour du réseau, et par le
  // bouton « Envoyer maintenant ».
  const envoyerLaFile = async (discret = false) => {
    if (!fileDisponible() || envoiFile) return;
    setEnvoiFile(true);
    try {
      const bilan = await vider((payload) => projectAPI.addTimeEntries(payload));
      await rafraichirAttente();
      if (bilan.envoyees) {
        toast({ title: `${bilan.envoyees} saisie(s) envoyée(s)`,
                description: 'Les heures sont arrivées sur le serveur.' });
        if (onglet === 'liste') chargerSaisies();
      } else if (!discret && bilan.restantes) {
        toast({ title: 'Toujours pas de réseau',
                description: `${bilan.restantes} saisie(s) attendent encore.` });
      }
    } catch (e) {
      console.warn('Envoi de la file impossible', e);
    } finally {
      setEnvoiFile(false);
    }
  };

  useEffect(() => {
    rafraichirAttente().then(() => envoyerLaFile(true));
    const auRetour = () => envoyerLaFile(true);
    window.addEventListener('online', auRetour);
    return () => window.removeEventListener('online', auRetour);
    /* eslint-disable-line */
  }, []);

  const chargerSaisies = async () => {
    setChargement(true);
    try {
      const depuis = new Date();
      depuis.setDate(depuis.getDate() - 30);
      setSaisies(await timeEntryAPI.list({ since: toLocalDateString(depuis) }));
    } catch (e) {
      toast({ title: 'Lecture impossible', variant: 'destructive' });
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => { if (onglet === 'liste') chargerSaisies(); /* eslint-disable-line */ }, [onglet]);

  const basculer = (id) => {
    setChoisis((actuels) => {
      const suivant = actuels.includes(id) ? actuels.filter((x) => x !== id) : [...actuels, id];
      if (!actuels.includes(id) && heures[id] === undefined) {
        setHeures((h) => ({ ...h, [id]: hoursToTimeStr(DEFAULT_HOURS) }));
      }
      return suivant;
    });
  };

  const prêt = date && projectId && task && choisis.length > 0
    && choisis.every((id) => timeStrToHours(heures[id]) > 0);

  const viderFormulaire = () => {
    setTask(''); setComment(''); setProblem(false);
    if (pourEquipe) { setChoisis([]); setHeures({}); }
  };

  const enregistrer = async () => {
    if (!prêt || envoiEnCours) return;
    setEnvoiEnCours(true);
    // L'identifiant est fabriqué AVANT l'envoi, même en réseau : si la réponse se
    // perd en route, le renvoi retrouvera le pointage déjà créé au lieu d'en
    // faire un second.
    const payload = {
      date, projectId, task, comment, problem,
      lines: choisis.map((id) => ({ employeeId: id, hours: timeStrToHours(heures[id]) })),
      clientBatchId: nouvelIdentifiant(),
    };
    const resume = `${formatHours(timeStrToHours(heures[choisis[0]]))} le ${formatDay(date)}`;
    try {
      await projectAPI.addTimeEntries(payload);
      toast({ title: 'Heures enregistrées', description: resume });
      viderFormulaire();
    } catch (e) {
      const statut = e.response?.status;
      if (statut && statut >= 400 && statut < 500) {
        // Le serveur a compris et refuse : la mettre en attente ne servirait à
        // rien, elle serait refusée pareil au prochain essai.
        toast({ title: 'Enregistrement refusé', description: e.response?.data?.detail || 'Réessayez',
                variant: 'destructive' });
      } else if (fileDisponible()) {
        await mettreEnAttente(payload);
        await rafraichirAttente();
        toast({ title: 'Gardé sur le téléphone',
                description: `${resume} — partira tout seul dès que le réseau revient.` });
        viderFormulaire();
      } else {
        toast({ title: 'Enregistrement impossible',
                description: "Pas de réseau, et ce téléphone ne sait pas garder la saisie.",
                variant: 'destructive' });
      }
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const oublierAttente = async (saisie) => {
    if (!window.confirm('Retirer définitivement cette saisie du téléphone ?')) return;
    await retirer(saisie.id);
    await rafraichirAttente();
  };

  const supprimer = async (entree) => {
    if (!window.confirm(`Supprimer ${formatHours(entree.hours)} du ${formatDay(entree.date)} ?`)) return;
    try {
      await timeEntryAPI.remove(entree.id);
      toast({ title: 'Saisie supprimée' });
      chargerSaisies();
    } catch (e) {
      toast({ title: 'Suppression refusée', description: e.response?.data?.detail || '',
              variant: 'destructive' });
    }
  };

  const enregistrerCorrection = async () => {
    try {
      await timeEntryAPI.update(correction.id, {
        date: correction.date, projectId: correction.projectId, employeeId: correction.employeeId,
        task: correction.task, hours: timeStrToHours(correction.heuresTexte),
        comment: correction.comment, problem: correction.problem,
      });
      toast({ title: 'Saisie corrigée' });
      setCorrection(null);
      chargerSaisies();
    } catch (e) {
      toast({ title: 'Correction refusée', description: e.response?.data?.detail || '',
              variant: 'destructive' });
    }
  };

  const seDeconnecter = async () => {
    try { await authAPI.logout(); } finally { onLoggedOut(); }
  };

  const joursRecents = useMemo(() => {
    const jours = [];
    for (let i = 0; i < 3; i += 1) {
      const j = new Date();
      j.setDate(j.getDate() - i);
      jours.push({
        valeur: toLocalDateString(j),
        libelle: i === 0 ? "Aujourd'hui" : i === 1 ? 'Hier' : j.toLocaleDateString('fr-FR', { weekday: 'long' }),
      });
    }
    return jours;
  }, []);

  if (!contexte) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
      </div>
    );
  }

  const salariesProposes = pourEquipe ? contexte.employees : contexte.employees.filter((e) => e.id === user.employeeId);

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-800">{user.displayName}</div>
            <div className="text-xs text-gray-500">
              {user.role === 'patron' ? 'Responsable'
                : user.role === 'chef' ? "Chef d'équipe"
                : moi?.fullName || 'Salarié'}
            </div>
          </div>
          {onRetour ? (
            <Button variant="ghost" size="sm" onClick={onRetour} className="text-gray-500">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={seDeconnecter} className="text-gray-500">
              <LogOut className="w-4 h-4 mr-1" /> Quitter
            </Button>
          )}
        </div>
        <div className="max-w-md mx-auto px-4 flex gap-2 pb-2">
          {[['saisie', pourEquipe ? 'Saisir des heures' : 'Saisir mes heures'],
            ['liste', pourEquipe ? 'Dernières saisies' : 'Mes dernières saisies']].map(([cle, libelle]) => (
            <button
              key={cle}
              onClick={() => setOnglet(cle)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                onglet === cle ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              {libelle}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        {enAttente.length > 0 && (
          <section className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-medium text-amber-900">
                <CloudOff className="w-4 h-4" />
                {enAttente.length} saisie{enAttente.length > 1 ? 's' : ''} en attente d'envoi
              </div>
              <Button size="sm" variant="outline" onClick={() => envoyerLaFile(false)}
                      disabled={envoiFile} className="bg-white">
                {envoiFile
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><Upload className="w-4 h-4 mr-1" /> Envoyer</>}
              </Button>
            </div>
            <p className="text-xs text-amber-800">
              Gardées dans le téléphone. Elles partiront toutes seules dès que le réseau revient.
            </p>
            {enAttente.map((saisie) => {
              const chantier = contexte.projects.find((p) => p.id === saisie.payload.projectId);
              const total = saisie.payload.lines.reduce((somme, l) => somme + l.hours, 0);
              return (
                <div key={saisie.id} className="bg-white rounded-lg border border-amber-200 p-2 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-800 truncate">
                        {chantier?.name || 'Chantier'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDay(saisie.payload.date)} · {saisie.payload.task} · {formatHours(total)}
                        {saisie.payload.lines.length > 1 && ` · ${saisie.payload.lines.length} salariés`}
                      </div>
                      {saisie.refus && (
                        <div className="text-xs text-red-600 mt-1">Refusée : {saisie.refus}</div>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => oublierAttente(saisie)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {onglet === 'saisie' && (
          <>
            <section className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Jour</div>
              <div className="flex gap-2 mb-2">
                {joursRecents.map((j) => (
                  <button
                    key={j.valeur}
                    onClick={() => setDate(j.valeur)}
                    className={`flex-1 py-2 rounded-lg text-sm capitalize border ${
                      date === j.valeur ? 'border-green-600 bg-green-50 text-green-800 font-medium'
                                        : 'border-gray-200 text-gray-600'}`}
                  >
                    {j.libelle}
                  </button>
                ))}
              </div>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11" />
            </section>

            <section className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Chantier</div>
              {contexte.projects.length === 0 && (
                <p className="text-sm text-gray-500">Aucun chantier en cours.</p>
              )}
              <div className="space-y-2">
                {contexte.projects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProjectId(p.id)}
                    className={`w-full text-left px-3 py-3 rounded-lg border ${
                      projectId === p.id ? 'border-green-600 bg-green-50' : 'border-gray-200'}`}
                  >
                    <div className="font-medium text-gray-800">{p.name}</div>
                    {p.clientName && <div className="text-xs text-gray-500">{p.clientName}</div>}
                  </button>
                ))}
              </div>
            </section>

            <section className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Tâche</div>
              <div className="flex flex-wrap gap-2">
                {TASKS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTask(t)}
                    className={`px-3 py-2 rounded-full text-sm border ${
                      task === t ? 'border-green-600 bg-green-50 text-green-800' : 'border-gray-200 text-gray-600'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </section>

            <section className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                {pourEquipe ? "Qui a travaillé, et combien" : 'Mes heures'}
              </div>
              <div className="space-y-2">
                {salariesProposes.map((e) => {
                  const actif = choisis.includes(e.id);
                  return (
                    <div key={e.id} className={`rounded-lg border p-3 ${actif ? 'border-green-600 bg-green-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between gap-3">
                        <button
                          onClick={() => (pourEquipe ? basculer(e.id) : null)}
                          className="text-left font-medium text-gray-800 flex-1"
                        >
                          {e.fullName || `${e.firstName} ${e.lastName}`}
                        </button>
                        {actif && (
                          <Input
                            type="time"
                            step="300"
                            value={heures[e.id] || ''}
                            onChange={(ev) => setHeures((h) => ({ ...h, [e.id]: ev.target.value }))}
                            className="w-28 h-11 text-lg"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <Textarea
                placeholder="Commentaire (facultatif)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
              />
              <button
                onClick={() => setProblem((p) => !p)}
                className={`w-full py-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 ${
                  problem ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-200 text-gray-600'}`}
              >
                <AlertTriangle className="w-4 h-4" /> Signaler un problème sur le chantier
              </button>
            </section>

            <Button
              onClick={enregistrer}
              disabled={!prêt || envoiEnCours}
              className="w-full h-14 text-base bg-green-600 hover:bg-green-700"
            >
              {envoiEnCours
                ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enregistrement…</>
                : <><CheckCircle2 className="w-5 h-5 mr-2" /> Enregistrer</>}
            </Button>
          </>
        )}

        {onglet === 'liste' && (
          <section className="space-y-3">
            {contexte.correctionDays && (
              <p className="text-xs text-gray-500">
                Corrigeable jusqu'à {contexte.correctionDays} jours en arrière ; au-delà, voyez avec le responsable.
              </p>
            )}
            {chargement && <Loader2 className="w-6 h-6 text-green-600 animate-spin mx-auto" />}
            {!chargement && saisies.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-8">Aucune saisie sur les 30 derniers jours.</p>
            )}
            {saisies.map((e) => (
              <div key={e.id} className="bg-white rounded-xl border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 truncate">{e.projectName}</div>
                    <div className="text-sm text-gray-500">
                      {formatDay(e.date)} · {e.task} · {formatHours(e.hours)}
                    </div>
                    {pourEquipe && <div className="text-xs text-gray-400">{e.employeeName}</div>}
                    {e.comment && <div className="text-xs text-gray-500 mt-1">{e.comment}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setCorrection({
                      ...e, heuresTexte: hoursToTimeStr(e.hours), comment: e.comment || '',
                      problem: !!e.problem })}>
                      <Pencil className="w-4 h-4 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => supprimer(e)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      {correction && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-800 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Corriger la saisie
              </div>
              <Button variant="ghost" size="sm" onClick={() => setCorrection(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Input type="date" value={correction.date}
                   onChange={(e) => setCorrection({ ...correction, date: e.target.value })} className="h-11" />
            <select
              value={correction.projectId}
              onChange={(e) => setCorrection({ ...correction, projectId: e.target.value })}
              className="w-full h-11 rounded-md border border-gray-200 px-3 text-sm"
            >
              {contexte.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select
              value={correction.task}
              onChange={(e) => setCorrection({ ...correction, task: e.target.value })}
              className="w-full h-11 rounded-md border border-gray-200 px-3 text-sm"
            >
              {TASKS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input type="time" step="300" value={correction.heuresTexte}
                   onChange={(e) => setCorrection({ ...correction, heuresTexte: e.target.value })}
                   className="h-11 text-lg" />
            <Textarea rows={2} value={correction.comment}
                      onChange={(e) => setCorrection({ ...correction, comment: e.target.value })} />
            <Button onClick={enregistrerCorrection} className="w-full h-12 bg-green-600 hover:bg-green-700">
              Enregistrer la correction
            </Button>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default MobileTimeEntry;
